@use "../../styles/breakpoint.module.scss" as *;

// Page-specific layout only. Colors come from the site's theme classes
// (applied in the template with getThemeClass) and color tokens; type, spacing,
// radius and shadows come from the site's typography, spacer, radius and
// elevation tokens.

// ── Shared ──

// A full-bleed band on the site grid, like ThemedModule.
.section {
    composes: repeater from "../../styles/layout.module.scss";
    grid-column: margin;
    padding-block: var(--spacer-64);

    > * {
        grid-column: content;
    }
}

.overline {
    composes: fontST16 from "../../styles/typography.module.scss";
    font-weight: 800;
    text-transform: uppercase;
    color: var(--overline-color, var(--poppy));
}

.sectionHeading {
    composes: fontHB80 from "../../styles/typography.module.scss";
    text-transform: uppercase;
}

.sectionHeader {
    margin-bottom: var(--spacer-40);
}

.body {
    composes: fontP20 from "../../styles/typography.module.scss";
}

// ── 1. Hero ──

.hero {
    composes: section;
    padding-block: var(--spacer-104);
}

.heroGrid {
    display: grid;
    grid-template-columns: subgrid;
    align-items: center;
    row-gap: var(--spacer-48);
}

.heroText {
    grid-column: col-start 1 / span 7;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacer-24);

    @media ($below-tablet) {
        grid-column: 1 / -1;
    }
}

.heroHeading {
    composes: fontHB104 from "../../styles/typography.module.scss";
    text-transform: uppercase;
}

.highlight {
    color: var(--poppy);
}

.heroButtons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--spacer-16);
    margin-top: var(--spacer-16);

    // The site's secondary button is smaller than the primary; side by side
    // here, give it the primary's type and padding so they match in height.
    .heroSecondary {
        font: var(--font-button-20);
        padding: var(--spacer-16) var(--spacer-32);
    }
}

.glance {
    --overline-color: var(--yellow);

    grid-column: col-start 8 / span 5;
    padding: var(--spacer-40);
    border-radius: var(--border-radius-lg);

    @media ($below-tablet) {
        grid-column: 1 / -1;
    }
}

.stats {
    composes: listReset from "../../styles/utils.module.scss";
    margin-top: var(--spacer-16);

    > li {
        padding-block: var(--spacer-24);

        + li {
            border-top: 1px solid currentcolor;
        }

        &:last-child {
            padding-bottom: 0;
        }
    }
}

.statValue {
    composes: fontHB64 from "../../styles/typography.module.scss";
    display: block;
    text-transform: uppercase;
}

.statLabel {
    composes: fontP16 from "../../styles/typography.module.scss";
    display: block;
    margin-top: var(--spacer-8);
}

// ── 2. Where to start ──

.steps {
    composes: listReset from "../../styles/utils.module.scss";
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--grid-gap);

    @media ($below-tablet) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media ($below-mobile) {
        grid-template-columns: 1fr;
    }
}

// Same corner shape as the site's colored cards (ColoredCardGrid).
.step {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-12);
    padding: var(--spacer-32) var(--spacer-24);
    border-radius: var(--border-radius-lg) var(--border-radius-lg)
        var(--border-radius-lg) 0;
}

.stepNumber {
    composes: fontHB80 from "../../styles/typography.module.scss";
    margin-bottom: var(--spacer-8);
}

.stepTitle {
    composes: fontST20 from "../../styles/typography.module.scss";
    font-weight: 800;
}

.stepBody {
    composes: fontP16 from "../../styles/typography.module.scss";
}

// ── 3–5. Resource groups ──

// Leaves room for the sticky header when "Browse all resources" jumps here.
.anchorTarget {
    scroll-margin-top: var(--spacer-80);
}

// Glide to it rather than jump, on this page only, unless the visitor asks
// for less motion.
@media (prefers-reduced-motion: no-preference) {
    :global(html):has(.anchorTarget) {
        scroll-behavior: smooth;
    }
}

.cards {
    composes: listReset from "../../styles/utils.module.scss";
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacer-24);

    @media ($below-mobile) {
        grid-template-columns: 1fr;
    }
}

// The whole card is the link. Hover matches the site's card hover
// (ColumnCarousel, GuidesCarousel): a --box-shadow-2 shadow.
.card {
    composes: base from "../../components/Clickables/index.module.scss";
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: var(--spacer-12);
    height: 100%;
    padding: var(--spacer-32);
    border-radius: var(--border-radius-lg);
    border: 1px solid var(--card-border, transparent);
    background: var(--card-bg, var(--color-primary));
    color: var(--card-text, var(--color-text));
    text-align: left;
    transition: box-shadow 0.3s ease;

    @media (hover: hover) {
        &:hover,
        &[data-hovered] {
            box-shadow: var(--box-shadow-2);
        }
    }

    // Clickables' base sizes every svg to the text; keep that, minus its
    // leading margin, which the action label handles itself.
    svg {
        margin-left: 0;
    }
}

// On indigo: a translucent white fill with a light border.
.cardTranslucent {
    --card-bg: rgb(255 255 255 / 8%);
    --card-border: rgb(255 255 255 / 30%);
    --card-action: var(--yellow);
}

.cardWhite {
    --card-bg: var(--white);
    --card-text: var(--black);
    --card-border: var(--mid-gray);
    --card-action: var(--poppy);
}

.cardBlack {
    --card-bg: var(--black);
    --card-text: var(--white);
    --card-action: var(--yellow);
}

.featured {
    --overline-color: var(--yellow);
    --card-action: var(--white);

    flex-direction: row;
    align-items: center;
    gap: var(--spacer-32);
    // A direct child of the section grid, so 100% height would swallow the
    // margin.
    height: auto;
    margin-bottom: var(--spacer-24);
    padding: var(--spacer-40);

    @media ($below-mobile) {
        flex-direction: column;
        align-items: flex-start;
    }
}

.featuredText {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-12);
    flex: 1;
}

.featuredTitle {
    composes: fontHB56 from "../../styles/typography.module.scss";
    text-transform: uppercase;
}

.cardTop {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacer-16);
}

.tag {
    composes: fontST12 from "../../styles/typography.module.scss";
    font-weight: 800;
    letter-spacing: 0.05em;
    padding: var(--spacer-4) var(--spacer-8);
    border: 1px solid currentcolor;
    border-radius: var(--border-radius-full);
}

.action {
    composes: fontB16 from "../../styles/typography.module.scss";
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-8);
    flex-shrink: 0;
    color: var(--card-action, var(--color-contrast));
}

.cardTitle {
    composes: fontST20 from "../../styles/typography.module.scss";
    font-weight: 800;
}

.cardBody {
    composes: fontP16 from "../../styles/typography.module.scss";
}

// ── 6. Facilitator training ──

.training {
    display: grid;
    grid-column: margin;
    grid-template-columns: 1fr 1fr;

    @media ($below-tablet) {
        grid-template-columns: 1fr;
    }
}

.trainingImage {
    position: relative;
    min-height: 400px;

    // ImageChooserBlock renders <picture><img>; fill the column.
    :is(picture, img) {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    @media ($below-tablet) {
        min-height: 0;
        aspect-ratio: 3 / 2;
    }
}

.trainingPanel {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: var(--spacer-24);
    padding: var(--spacer-104) var(--container-margin) var(--spacer-104)
        var(--spacer-80);

    // Without a photo the panel spans the band, so pad it like the grid.
    &:only-child {
        grid-column: 1 / -1;
        padding-inline: var(--container-margin);
    }

    @media ($below-tablet) {
        padding: var(--spacer-64) var(--container-margin);
    }
}

.trainingDate {
    composes: fontST20 from "../../styles/typography.module.scss";
    font-weight: 800;
}

// ── 7. Newsletter + contact ──

// Navy, not black, so the band doesn't run into the black footer.
.connect {
    --overline-color: var(--yellow);
}

.connectGrid {
    display: grid;
    grid-template-columns: subgrid;
    row-gap: var(--spacer-48);
}

.newsletter {
    grid-column: col-start 1 / span 6;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-24);

    @media ($below-tablet) {
        grid-column: 1 / -1;
    }
}

// Same filled field as the site's newsletter module (Newsletter.module.scss),
// so the input reads on the black band.
.form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: var(--spacer-16);

    fieldset {
        display: contents;
    }
}

.input {
    --color-text: var(--black);

    flex: 1 1 280px;

    input {
        background: var(--white);
        color: var(--black);
        padding-inline: var(--spacer-16);
        padding-block-start: var(--spacer-24);
        border-radius: var(--border-radius-sm);
    }

    div:has(> input) {
        border-bottom-color: transparent;
    }

    label {
        left: var(--spacer-16);
        margin-top: var(--spacer-8);
    }
}

.formMessage {
    composes: fontST16 from "../../styles/typography.module.scss";
    flex-basis: 100%;
}

.formError {
    composes: formMessage;
    color: var(--color-invalid, var(--poppy));
}

.contact {
    grid-column: col-start 8 / span 5;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-16);
    padding-left: var(--grid-gap);
    border-left: 1px solid var(--dark-gray);

    @media ($below-tablet) {
        grid-column: 1 / -1;
        padding-left: 0;
        padding-top: var(--spacer-48);
        border-left: 0;
        border-top: 1px solid var(--dark-gray);
    }
}

.contactText {
    composes: fontSH32 from "../../styles/typography.module.scss";
}

.contactLink {
    composes: fontP20 from "../../styles/typography.module.scss";
    align-self: flex-start;
}
